import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { IntlShape, useIntl } from "react-intl";
import clsx from "clsx";
import { shallowEqual } from "react-redux";
import ReactTooltip from "react-tooltip";

import { DealFilterBar } from "@app/modules/deal-filter-bar/deal-filter-bar.component";
import { ModalComponent } from "@app/core/modal";
import { DeleteModal } from "@app/modules/delete-modal/delete-modal.component";
import { OutDealDTO, OutFilterDTO, OutDealDTOCreatedByEnum, InExternalDealFilterDTO } from "@app/api/generated";
import { Table } from "@app/components/table/table";
import { Column } from "@webbio/components";
import { LabelComponent } from "@app/core";
import { platformTranslationKeys } from "@app/constants/platform";
import { IDealEditOptions } from "@app/constants/deal";
import { useAppSelector, useAppDispatch } from "@app/redux/store";
import { dealsThunks } from "@app/redux/thunks/deals.thunk";
import { setCurrentPage } from "@app/redux/reducers/deals";
import { useDateFnsFormat } from "@app/util/use-date-fns-format";
import { ROUTES } from "@app/constants/routes";
import { BetterModal } from "@app/core/better-modal/better-modal";
import { Overview } from "@app/components/overview/overview";
import { TableActions } from "@app/core/table-actions";
import { useOnButtonLinkClick } from "@app/util/use-on-button-link-click";
import { useFilterState } from "@app/util/use-filter-state";

import { DealOverviewOptionsBar } from "./options-bar/deal-overview-options-bar-component";
import styles from "./deal-overview-component.module.scss";
import tableStyles from "./table-styling-component.module.scss";

const TAKE = 30;

const DealOverview = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {
    deals,
    isLoadingDeals,
    totalResults,
    currentMerchant,
    currentPlatform,
    currentSearch,
    currentStatus,
    currentCreatedBy,
    currentPage
  } = useAppSelector((state) => state.deals, shallowEqual);
  const { formatDate } = useDateFnsFormat();

  const { onButtonLinkClick } = useOnButtonLinkClick();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isDeleteDealOpen, setIsDeleteDealOpen] = useState<boolean>(false);
  const [isDeleteBulkDealsOpen, setIsDeleteBulkDealsOpen] = useState<boolean>(false);
  const [dealToDelete, setDealToDelete] = useState<OutDealDTO | undefined>(undefined);
  const [bulkDealsToDelete, setBulkDealsToDelete] = useState<string | undefined>(undefined);
  const [selectedInTable, setSelectedInTable] = useState<number[]>([]);
  const [selectedEDFS, setSelectedEDFS] = useState<InExternalDealFilterDTO[]>();

  useFilterState({
    deps: { currentPlatform, currentSearch, currentStatus, currentCreatedBy, currentMerchant },
    onFirstChange: () => getDeals(currentPage),
    onChange: () => getDeals(1)
  });

  useEffect(() => {
    setTotalPages(Math.ceil(totalResults / TAKE));
  }, [totalResults]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [deals]);

  const onDeleteDeal = (deal?: OutDealDTO, bulkDeals?: string) => {
    if (deal) {
      setDealToDelete(deal);
      setIsDeleteDealOpen(true);
    } else if (bulkDeals) {
      setBulkDealsToDelete(bulkDeals);
      setIsDeleteBulkDealsOpen(true);
    }
  };

  const onDeleteConfirmed = async () => {
    if (dealToDelete) {
      await dispatch(dealsThunks.deleteDeal(dealToDelete.id));

      setIsDeleteDealOpen(false);
    } else if (bulkDealsToDelete) {
      await dispatch(dealsThunks.deleteBulkDeals(bulkDealsToDelete));

      setIsDeleteBulkDealsOpen(false);
      setSelectedInTable([]);
      getDeals();
    }
  };

  const getDeals = (newPage?: number) => {
    const page = newPage || currentPage;
    const skip = (page - 1) * TAKE;

    dispatch(
      dealsThunks.getDeals(skip, TAKE, currentMerchant, currentPlatform, currentSearch, currentStatus, currentCreatedBy)
    );
    dispatch(setCurrentPage(page));

    window.scrollTo(0, 0);
  };

  const onPageChange = (page: number) => {
    getDeals(page);
    setSelectedInTable([]);
  };

  const handleActionRemove = async (): Promise<void> => {
    let payload = "";

    selectedInTable.forEach((id, index) => {
      if (deals && selectedInTable.length === index + 1) {
        payload += `${deals[id].id}`;
      } else if (deals) {
        payload += `${deals[id].id};`;
      }
    });
    onDeleteDeal(undefined, payload);
  };

  const handleActionEdit = async (changes: IDealEditOptions): Promise<void> => {
    const dealsToEdit =
      deals &&
      selectedInTable
        .map((index) => deals[index])
        .filter(Boolean)
        .map((deal) => toBulkPatchDeal(deal, changes));

    if (dealsToEdit && dealsToEdit.length > 0) {
      await dispatch(dealsThunks.editBulkDeals({ deals: dealsToEdit }));
    }

    setSelectedInTable([]);
    getDeals();
  };

  const handleTableSelect = async (items: number[]): Promise<void> => {
    setSelectedInTable([...items]);
  };

  const getBrandsString = (brands: OutFilterDTO[] | undefined) => {
    let brandNames: any = [];
    if (brands && brands.length > 0) {
      brands.map((brand) => {
        brandNames = [...brandNames, brand.name];
      });

      return brandNames.join(", ");
    }

    return "-";
  };

  const getCategoriesString = (categories: OutFilterDTO[] | undefined) => {
    let categoryNames: any = [];
    if (categories && categories.length > 0) {
      categories.map((category) => {
        categoryNames = [...categoryNames, category.name];
      });

      return categoryNames.join(", ");
    }

    return "-";
  };

  const onTableRowAuxClick = (row: OutDealDTO, e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) =>
    onTableRowClick(row, e, true);

  const onTableRowClick = (
    row: OutDealDTO,
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    forceTargetBlank?: boolean
  ) => {
    const route = `${ROUTES.DEAL_DETAIL}/${row.id}`;

    onButtonLinkClick(route, e, forceTargetBlank);
  };

  return (
    <Overview
      title={intl.formatMessage({ id: "dealFilterBarComponent.title" }, { total: intl.formatNumber(totalResults) })}
      actionBar={<DealFilterBar />}
      isLoading={isLoadingDeals}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      overviewItems={deals}
      header={
        <DealOverviewOptionsBar onEdit={handleActionEdit} onRemove={handleActionRemove} isSelected={selectedInTable} />
      }
    >
      {deals && deals.length > 0 && (
        <Table dataSource={deals || []} isLoading={isLoadingDeals} onSelect={handleTableSelect}>
          <Column
            title={getColumns(intl).status.title}
            field={getColumns(intl).status.field}
            id={getColumns(intl).status.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(live): JSX.Element => {
              return (
                <span className={clsx(tableStyles.status)}>
                  <LabelComponent type={live ? "live" : "offline"} />
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).name.title}
            field={getColumns(intl).name.field}
            id={getColumns(intl).name.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(name): JSX.Element => {
              return (
                <span className={clsx("truncate-table", tableStyles.name)} title={name}>
                  {name || "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).ean.title}
            field={getColumns(intl).ean.field}
            id={getColumns(intl).ean.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            maxWidth={132}
            width={132}
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <span className={clsx("truncate-table", tableStyles.ean)} title={deal.ean}>
                  {deal.ean || "—"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).store.title}
            field={getColumns(intl).store.field}
            id={getColumns(intl).store.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <span
                  className={clsx("truncate-table", tableStyles.store)}
                  title={deal.merchant && deal.merchant?.toString()}
                >
                  {deal.merchant?.name || "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).brand.title}
            field={getColumns(intl).brand.field}
            id={getColumns(intl).brand.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <span className={clsx("truncate-table", tableStyles.brand)} title={deal.brands?.toString()}>
                  {getBrandsString(deal.brands)}
                </span>
              );
            }}
          />
          <Column
            title={getColumns(intl).category.title}
            field={getColumns(intl).category.field}
            id={getColumns(intl).category.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <span className={clsx("truncate-table", tableStyles.category)} title={deal.categories?.toString()}>
                  {getCategoriesString(deal.categories)}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).sponsored.title}
            field={getColumns(intl).sponsored.field}
            id={getColumns(intl).sponsored.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <span className={clsx("truncate-table", tableStyles.sponsored)}>{deal.sponsoredPosition || "-"}</span>
              );
            }}
          />
          <Column
            title={getColumns(intl).lastEdited.title}
            field={getColumns(intl).lastEdited.field}
            id={getColumns(intl).lastEdited.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <span className={clsx("truncate-table", tableStyles.lastEdited)}>
                  {deal.updatedAtByUser ? formatDate(deal.updatedAtByUser, "eeeeee. dd MMM yyyy HH:mm") : "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).platform.title}
            field={getColumns(intl).platform.field}
            id={getColumns(intl).platform.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              const platformString =
                deal &&
                deal.platform &&
                platformTranslationKeys.find((platformItem) => platformItem.id === deal.platform.id);

              return (
                <span className={clsx("truncate-table", tableStyles.platform)}>
                  {platformString ? intl.formatMessage({ id: platformString.name }) : "-"}
                </span>
              );
            }}
          />

          <Column
            title={getColumns(intl).addedBy.title}
            field={getColumns(intl).addedBy.field}
            id={getColumns(intl).addedBy.id}
            onTdClick={onTableRowClick}
            onTdAuxClick={onTableRowAuxClick}
            cell={(_, __, deal: OutDealDTO) => {
              const createdBy = (value: OutDealDTOCreatedByEnum) => {
                switch (value) {
                  case OutDealDTOCreatedByEnum.API:
                    return intl.formatMessage({ id: "global.createdBy.api" });
                  case OutDealDTOCreatedByEnum.DASHBOARD:
                    return intl.formatMessage({ id: "global.createdBy.dashboard" });
                  case OutDealDTOCreatedByEnum.UNKNOWN:
                    return intl.formatMessage({ id: "global.createdBy.unknown" });
                  default:
                    return intl.formatMessage({ id: "global.createdBy.unknown" });
                }
              };

              return (
                <span className={clsx("truncate-table", tableStyles.addedBy)}>{createdBy(deal.createdBy as any)}</span>
              );
            }}
          />
          <Column
            title={getColumns(intl).edf.title}
            field={getColumns(intl).edf.field}
            id={getColumns(intl).edf.id}
            minWidth={64}
            onTdClick={(row: OutDealDTO, e) => {
              if (!row.externalDealFilters || row.externalDealFilters.length === 0) {
                onTableRowClick(row, e);
              }
            }}
            cell={(edfs: InExternalDealFilterDTO[]) => {
              if (!edfs || edfs.length === 0) {
                return <></>;
              }

              const edf = edfs[0];

              return (
                <div className={tableStyles.edf}>
                  {edfs.length === 1 ? (
                    <Link
                      className="truncate-table"
                      to={`${ROUTES.EXTERNAL_DEAL_FILTERS}/${edf.id}`}
                      data-tip={edf.title}
                      data-for="BlackFriday"
                    >
                      {edf.id}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSelectedEDFS(edfs)}
                      data-tip={intl.formatMessage({ id: "dealOverviewComponent.table.edf.multiple.tooltip" })}
                      data-for="BlackFriday"
                    >
                      <span className="truncate-table">{edfs.map((x) => x.id).join(", ")}</span>
                    </button>
                  )}
                </div>
              );
            }}
          />
          <Column
            title=""
            field="id"
            cell={(_, __, deal: OutDealDTO) => {
              return (
                <TableActions
                  editLink={`${ROUTES.DEAL_DETAIL}/${deal.id}`}
                  onDelete={() => onDeleteDeal(deal, undefined)}
                />
              );
            }}
          />
        </Table>
      )}

      <ModalComponent
        title={intl.formatMessage({ id: "dealOverviewComponent.leavePageConfirmation" })}
        isModalOpen={isDeleteDealOpen}
        onCloseModal={() => {
          setIsDeleteDealOpen(false);
        }}
        variant="big"
      >
        <DeleteModal
          onCancel={() => {
            setIsDeleteDealOpen(false);
          }}
          onDelete={onDeleteConfirmed}
        />
      </ModalComponent>

      <ModalComponent
        title={intl.formatMessage({ id: "dealOverviewComponent.leavePageConfirmation.bulkDeals" })}
        isModalOpen={isDeleteBulkDealsOpen}
        onCloseModal={() => {
          setIsDeleteBulkDealsOpen(false);
        }}
        variant="big"
      >
        <DeleteModal
          onCancel={() => {
            setIsDeleteBulkDealsOpen(false);
          }}
          onDelete={onDeleteConfirmed}
        />
      </ModalComponent>

      <BetterModal isOpen={Boolean(selectedEDFS)} onClose={() => setSelectedEDFS(undefined)} maxWidth={400}>
        <div className={styles.filterModalList}>
          {selectedEDFS?.map((edf) => (
            <Link key={edf.id} to={`${ROUTES.EXTERNAL_DEAL_FILTERS}/${edf.id}`} className={styles.filterModalItem}>
              <span className={styles.idLabel}>{edf.id}</span>
              <span>{edf.title}</span>
            </Link>
          ))}
        </div>
      </BetterModal>
    </Overview>
  );
};

/**
 * One deal of a PATCH /deals/bulk request. The API writes only the fields that are present, so a field the editor
 * did not change is left out. Brands, categories and EDFs of the list row stay out as well, so a bulk edit never
 * writes an outdated copy of them back. The name stays in: the API rebuilds the slug from it.
 */
const toBulkPatchDeal = (deal: OutDealDTO, changes: IDealEditOptions): OutDealDTO => ({
  ...deal,
  brands: undefined,
  categories: undefined,
  externalDealFilters: undefined,
  // undefined drops the field from the request body.
  live: (changes.status ? changes.status === 1 : undefined) as boolean,
  // undefined leaves the label, null removes it.
  featuredFilter: (changes.featuredFilterId === undefined
    ? undefined
    : changes.featuredFilterId === null
    ? null
    : { id: changes.featuredFilterId }) as OutFilterDTO
});

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getColumns = (intl: IntlShape) => ({
  status: {
    id: "status",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderStatus" }),
    field: "live"
  },
  name: {
    id: "name",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderName" }),
    field: "name"
  },
  ean: {
    id: "ean",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderEan" }),
    field: "ean"
  },
  store: {
    id: "store",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderStore" }),
    field: "store"
  },
  brand: {
    id: "brand",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderBrand" }),
    field: "brand"
  },
  category: {
    id: "category",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderCategory" }),
    field: "category"
  },
  sponsored: {
    id: "sponsored",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderSponsored" }),
    field: "sponsored"
  },
  lastEdited: {
    id: "lastEdited",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderLastEdited" }),
    field: "lastEdited"
  },
  platform: {
    id: "platform",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderPlatform" }),
    field: "platform"
  },
  addedBy: {
    id: "addedBy",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderAddedBy" }),
    field: "addedBy"
  },
  edf: {
    id: "externalDealFilters",
    title: intl.formatMessage({ id: "dealOverviewComponent.tableHeaderEDF" }),
    field: "externalDealFilters"
  }
});

export { DealOverview };
